import { sb } from '../supabase';
import { buildThumbResolver } from './requestAttachmentUrls';
import { isHeldForVerification } from './offerVisibility';
import { isRequestOfferable } from './requestDistribution';
import { fetchRequestFees, isFeeSettled } from './requestFee';
// The ONE list of "a deal exists", shared with the distribute panel's
// refusal wording. Imported rather than restated: a status list written
// twice is two places to forget the next one (golden rule 12).
import { REQUEST_DEAL_STATUSES } from './requestStatus';

// Unified quotation board: every request a trader made, whichever way they made
// it. `requests` rows carry request_kind 'factory' | 'managed', or neither with
// sourcing_mode 'direct'. This joins the split signals — offers,
// request_factory_invites, request_customization, trader profile, target factory
// — and derives ONE status vocabulary the founder asked for.
//
// SIX values as of 2026-09-22: waiting · replied · accepted · completed ·
// rejected · cancelled. It was four, and `closed` covered the middle three at
// once — so an accepted offer, a finished deal and a rejected request all read
// as one grey «مغلق».
//
// Direct requests were excluded until 2026-09-18, which is why TECH_DEBT carried
// "the admin console has no list of DIRECT requests": a trader could post an
// open request for offers and no admin screen would ever show it. They are now
// in, and the distribute button on each row is what an admin does about one.

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
// CANCELLED IS ITS OWN OUTCOME, not a kind of closed. "A deal that completed"
// and "a trader who walked away" are different business facts, and collapsing
// them loses the one worth counting. Both spellings, because the column has
// carried both.
const CANCELLED = ['cancelled', 'canceled'];

// ══ THE BUCKET THAT USED TO BE ONE ═══════════════════════════════════
// CLOSED was ['closed','completed','fulfilled','delivered','rejected'] and the
// board printed one grey «مغلق» over all five. Three unrelated endings read
// alike: the trader ACCEPTED an offer, the deal FINISHED, and the request was
// REJECTED. The first is the best outcome on the board and it looked like the
// worst.
//
// Split into three, each a list of what it IS. Order of evaluation matters and
// is asserted below, because two of these lists legitimately overlap.
const REJECTED = ['rejected'];

// A finished deal. 'delivered' lives HERE rather than with the deal statuses
// below, deliberately: it is the last thing that happens, and an admin reading
// the board wants "done", not "an offer was accepted eleven steps ago".
const COMPLETED = ['completed', 'fulfilled', 'delivered'];

// A deal EXISTS. Everything from the accept up to — but not including —
// delivery.
//
// ⚠️ Four of these were NOT in the old CLOSED list: paid, ready_to_ship,
// shipping and arrived. They fell through to `replied` or `waiting`, so a PAID
// request advertised itself on the board as still waiting for a supplier. No
// row is in those states today (production, 2026-09-22: only open, cancelled,
// offers_received and one closed exist), so nothing visibly moves — but the
// mislabel was real and is fixed here rather than left to surface later.
//
// Derived from REQUEST_DEAL_STATUSES so this board and
// requestDistributionBlockReason() cannot drift apart: one list, two readers.
const ACCEPTED = REQUEST_DEAL_STATUSES.filter((s) => !COMPLETED.includes(s));

/**
 * Which board bucket a request belongs in.
 *
 * Exported and PURE so it can be tested without a database. The bucketing used
 * to live inline inside fetchQuotations(), where the only way to exercise it
 * was to have a row in that state in production — and four of the six states
 * have never existed there, so in practice it could not be exercised at all.
 *
 * `replied` is passed in rather than derived here: it depends on offers and
 * invites this function does not see.
 *
 * ⚠️ ORDER IS LOAD-BEARING, and every rung is a positive test (golden rule
 * 12 — name the states you act on, so a status invented later falls through
 * to waiting rather than silently joining a bucket).
 *   cancelled  first and independently: a cancelled request can also carry
 *              offers, and "supplier replied" is not what the board should say
 *              about one the trader walked away from.
 *   rejected   kept with the other dead ending so the chain reads in outcome
 *              order.
 *   completed  BEFORE accepted, because 'delivered' is a deal status too.
 *              ACCEPTED is already built by subtracting COMPLETED, so this is
 *              belt and braces — kept because the two defences fail in
 *              opposite directions if either list is edited carelessly.
 *   accepted   a deal exists.
 */
export function quotationBoardStatus({ status, managed_status: managedStatus } = {}, replied = false) {
  const inList = (list) => list.includes(status) || list.includes(managedStatus);
  if (inList(CANCELLED)) return 'cancelled';
  if (inList(REJECTED)) return 'rejected';
  if (inList(COMPLETED)) return 'completed';
  if (inList(ACCEPTED)) return 'accepted';
  return replied ? 'replied' : 'waiting';
}
const safe = (p) => p.then((r) => r, () => ({ data: [] }));

export async function fetchQuotations() {
  const { data: reqs, error } = await sb.from('requests')
    .select('id, title_ar, title_en, title_zh, category, quantity, unit, budget_per_unit, budget_currency, request_kind, sourcing_mode, status, managed_status, response_deadline, reference_image, attachment_paths, buyer_id, created_at, last_activity_at')
    .or('request_kind.in.(factory,managed),sourcing_mode.eq.managed,sourcing_mode.eq.direct')
    .order('created_at', { ascending: false }).limit(300);
  if (error) throw error;
  if (!reqs || !reqs.length) return [];

  const ids = reqs.map((r) => r.id);
  const buyerIds = [...new Set(reqs.map((r) => r.buyer_id).filter(Boolean))];

  const [{ data: offers }, { data: invites }, { data: cust }, { data: buyers }, { data: routing }] = await Promise.all([
    // managed_visibility + visibility_reason ride along on a query that was
    // already running, so the board can show which requests have a quote HELD
    // from the trader without a second round trip or an RPC per row.
    //
    // There is no visibility filter here and never was: offers_select gives an
    // admin every row (20260918172830), which is why `replied` below has always
    // counted held offers too. That part was already right — what was missing
    // is that the board could not say WHY a request was waiting.
    safe(sb.from('offers').select('request_id, status, managed_visibility, visibility_reason').in('request_id', ids)),
    safe(sb.from('request_factory_invites').select('request_id, factory_id, status, slug').in('request_id', ids)),
    safe(sb.from('request_customization').select('request_id, attachment_paths, ship_country, ship_city').in('request_id', ids)),
    buyerIds.length ? safe(sb.from('profiles').select('id, full_name, company_name, country').in('id', buyerIds)) : Promise.resolve({ data: [] }),
    // How many suppliers can actually SEE each request. An unrouted request is
    // invisible to every supplier, so zero here means the request is sitting
    // there doing nothing — which is exactly what the board needs to show at a
    // glance. Since 2026-10-11 an open direct request is routed to every
    // operational supplier automatically (source 'open_pool'), so its count is
    // the whole pool; zero now flags a managed or targeted request nobody
    // has been routed to.
    safe(sb.from('request_routing').select('request_id, source').in('request_id', ids)),
  ]);

  const offerSet = new Set((offers || []).map((o) => o.request_id));
  // Held-per-request, counted with the SAME predicate the detail screen and
  // get_request_pending_review_count use — see src/lib/offerVisibility.js.
  //
  // The request is passed per offer, because a curated request counts zero:
  // there the trader is shown one negotiated price and raw quotes are never
  // released, so a "held" chip would point at work that approving nobody can
  // clear. The server-side count returns 0 for the same requests.
  const reqById = {}; reqs.forEach((r) => { reqById[r.id] = r; });
  const heldBy = {};
  (offers || []).forEach((o) => {
    if (isHeldForVerification(o, reqById[o.request_id])) {
      heldBy[o.request_id] = (heldBy[o.request_id] || 0) + 1;
    }
  });
  const inviteBy = {}; (invites || []).forEach((i) => { if (!inviteBy[i.request_id]) inviteBy[i.request_id] = i; });
  const custBy = {}; (cust || []).forEach((c) => { custBy[c.request_id] = c; });
  const buyerBy = {}; (buyers || []).forEach((b) => { buyerBy[b.id] = b; });
  const routedCount = {}; (routing || []).forEach((r) => { routedCount[r.request_id] = (routedCount[r.request_id] || 0) + 1; });

  const facIds = [...new Set((invites || []).map((i) => i.factory_id).filter(Boolean))];
  let facBy = {};
  if (facIds.length) {
    const { data: facs } = await safe(sb.from('factory_directory').select('id, company_name, company_name_latin, phone').in('id', facIds));
    (facs || []).forEach((f) => { facBy[f.id] = f; });
  }

  // Attachments live in TWO places — requests.attachment_paths and
  // request_customization.attachment_paths — and this board has to merge them for
  // the same reason get_factory_invite does (migration 20260916094415) and
  // factory-attachment-url does: a factory RFQ written through createFactoryRequest
  // lands in both, while paths 2-4 write only the first. Reading one side would
  // silently under-count the attachments and pick the wrong thumbnail.
  const pathsOf = (r) => {
    const c = custBy[r.id];
    const merged = [
      ...(Array.isArray(r.attachment_paths) ? r.attachment_paths : []),
      ...(Array.isArray(c?.attachment_paths) ? c.attachment_paths : []),
    ].filter((p) => typeof p === 'string' && p !== '');
    return [...new Set(merged)];
  };

  // One batched signing call for the whole board, before the rows are mapped.
  const thumb = await buildThumbResolver(reqs, pathsOf);
  // Request fees (2026-09-29): an unpaid one is not distributable.
  const feeBy = await fetchRequestFees(sb, ids);

  return reqs.map((r) => {
    const inv = inviteBy[r.id];
    const fac = inv ? facBy[inv.factory_id] : null;
    const c = custBy[r.id];
    const replied = offerSet.has(r.id) || inv?.status === 'offer_submitted';
    return {
      id: r.id,
      // FOUR kinds, because there are four different jobs behind them and the
      // admin does something different with each. This used to be
      //   r.request_kind === 'factory' ? 'factory' : 'managed'
      // which labelled everything else "managed" -- so an open RFQ a trader
      // posted, and an idea-to-product brief, both appeared as managed orders,
      // and the Open button sent them to the managed-order screen. One of those
      // two only started showing up on 2026-09-18, when direct requests were
      // added to this board, which is how the collapse became visible.
      //
      //   managed          the concierge service: we run the whole order
      //   idea_to_product  the same service, but starting from an idea
      //   factory          a quote asked of one named factory
      //   open             posted to the market, nobody targeted
      kind: r.request_kind === 'factory' ? 'factory'
        : r.request_kind === 'idea_to_product' ? 'idea_to_product'
          : r.request_kind === 'managed' || r.sourcing_mode === 'managed' ? 'managed'
            : 'open',
      // The request's own category code. Carried so a screen can preselect the
      // distribution filter with it instead of making the admin re-pick what the
      // trader already told us.
      category: r.category || null,
      title_ar: r.title_ar, title_en: r.title_en, title_zh: r.title_zh,
      quantity: r.quantity, unit: r.unit,
      target_price: r.budget_per_unit, currency: r.budget_currency,
      // Signed URL for the first image attachment, falling back to the legacy
      // public reference_image so pre-retirement rows keep their picture.
      image: thumb(r), deadline: r.response_deadline, created_at: r.created_at,
      last_activity_at: r.last_activity_at || r.created_at,
      trader: buyerBy[r.buyer_id] || null,
      factory: fac ? { id: fac.id, name: nf(fac.company_name) || nf(fac.company_name_latin), phone: fac.phone } : null,
      invite_slug: inv?.slug || null,
      attachments: pathsOf(r).length,
      routedCount: routedCount[r.id] || 0,
      ship_to: [nf(c?.ship_city), nf(c?.ship_country)].filter(Boolean).join('، ') || null,
      // Cancelled is tested FIRST and independently of `closed`: a cancelled
      // request can also carry offers, and "supplier replied" on a request the
      // trader walked away from is not what the board should be showing.
      // ⚠️ THIS `status` IS NOT requests.status. It is a DERIVED board
      // vocabulary — waiting / replied / accepted / completed / rejected /
      // cancelled — and it OVERWRITES the raw column of the same name. Any consumer that reads `row.status`
      // expecting the database value gets this instead, silently.
      //
      // That cost a production incident on 2026-09-20: the distribute buttons
      // greyed themselves on healthy requests because isRequestOfferable() was
      // handed this row and tested 'replied' against ['open','offers_received'].
      // M-10073 was live and could not be distributed — worse than the bug
      // being fixed.
      //
      // The fix is `offerable` below, computed from the RAW row before this
      // line shadows it. DO NOT reintroduce a consumer that infers offerability
      // from `status` here.
      // The bucketing rules, and the reason their ORDER matters, are on
      // quotationBoardStatus() above. It is a separate exported function so
      // the four states production has never produced can still be tested.
      status: quotationBoardStatus(r, replied),
      // The RAW requests.status, kept because the line above SHADOWS it and
      // nothing downstream could get it back. DistributePanel needs it to say
      // WHY a request is not distributable — "cancelled" and "an offer was
      // accepted" are opposite facts that the derived `closed` bucket above
      // merges, along with completed/fulfilled/delivered/rejected.
      //
      // It is for WORDING ONLY. Offerability is `offerable` below and is
      // enforced again server-side; do not start testing this field instead.
      raw_status: r.status,
      // Computed from r.status — the real column — NOT from the derived value
      // above. Passed to the panels as a plain boolean so no component has to
      // know which shape of row it was handed.
      offerable: isRequestOfferable(r) && isFeeSettled(feeBy[r.id]),
      // The request fee's state, for the board's label. null = no fee.
      feeStatus: feeBy[r.id]?.status || null,
      // Quotes on this request the trader cannot see until a supplier is
      // approved. Zero for every request with nothing held, so the board stays
      // quiet in the normal case.
      heldCount: heldBy[r.id] || 0,
    };
  });
}
